import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { IUserDevice, IUserDeviceCommand } from '@platfio/interfaces/platfio';
import { Observable, Subscription } from 'rxjs';
import { DesktopDeviceCommandRunnerService } from 'src/app/services/desktop-device-command-runner.service';
import { AuthService } from 'src/app/services/auth.service';
import { UiPopoverService } from 'src/app/components/elements/ui-popover/ui-popover.service';
import { NativeHostService } from 'src/app/services/native-host.service';
import { ThemeService } from 'src/app/services/theme.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserDeviceService } from 'src/app/services/user-device.service';
import { ChatDeviceCommandTarget } from '../../interfaces/chat-component.interfaces';

type ChatDevicesMobilePanel = 'devices' | 'commands' | 'details';

interface DeviceCommandExecutionAttempt {
  attempt: number;
  completedAt: string;
  error?: string;
  exitCode?: number | null;
  retryable: boolean;
  startedAt: string;
  status: 'completed' | 'failed';
  summary: string;
}

interface DeviceCommandExecutionMetadata {
  attemptCount?: number;
  attempts?: DeviceCommandExecutionAttempt[];
  lastAttemptStatus?: string;
  lastError?: string;
  maxAttempts?: number;
  retryable?: boolean;
  strategy?: string;
  willRetry?: boolean;
}

@Component({
  selector: 'app-chat-devices-modal',
  templateUrl: './chat-devices-modal.component.html',
  styleUrls: ['./chat-devices-modal.component.scss'],
  host: { '[class.theme-light]': 'theme.isLight' }
})
export class ChatDevicesModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() initialCommandTarget: ChatDeviceCommandTarget | null = null;
  @ViewChild('addDevicePopoverTemplate') addDevicePopoverTemplate?: TemplateRef<any>;

  devices: IUserDevice[] = [];
  deviceCommands: IUserDeviceCommand[] = [];
  devicesLoading = true;
  deviceCommandsLoading = false;
  devicesError = '';
  deviceCommandsError = '';
  isElectronDesktop = false;
  desktopDeviceId = '';
  desktopDeviceName = '';
  desktopDeviceAgencyShared = false;
  registeringDesktopDevice = false;
  isAddDevicePopoverOpen = false;
  selectedDeviceId = '';
  selectedDeviceCommandId = '';
  selectedDeviceCommandLiveOutput$?: Observable<string>;
  activeMobilePanel: ChatDevicesMobilePanel = 'devices';

  private authSubscription?: Subscription;
  private desktopAvailabilitySubscription?: Subscription;
  private devicesSubscription?: Subscription;
  private deviceCommandsSubscription?: Subscription;
  private pendingInitialCommandTarget: ChatDeviceCommandTarget | null = null;
  private uid = '';

  constructor(
    private authService: AuthService,
    private userDeviceService: UserDeviceService,
    private desktopDeviceCommandRunnerService: DesktopDeviceCommandRunnerService,
    private popoverService: UiPopoverService,
    private nativeHostService: NativeHostService,
    private toastService: ToastService,
    public theme: ThemeService
  ) {}

  ngOnInit(): void {
    this.pendingInitialCommandTarget = this.initialCommandTarget;
    this.initializeDesktopDeviceDraft();

    this.desktopAvailabilitySubscription = this.nativeHostService.desktopAvailable$.subscribe(() => {
      this.initializeDesktopDeviceDraft();
    });

    this.authSubscription = this.authService.afAuth.authState.subscribe((user) => {
      const nextUid = user?.uid || '';
      if (this.uid === nextUid) {
        return;
      }

      this.uid = nextUid;
      this.subscribeToDevices();
    });
  }

  ngOnDestroy(): void {
    this.closeAddDevicePopover();
    this.authSubscription?.unsubscribe();
    this.desktopAvailabilitySubscription?.unsubscribe();
    this.devicesSubscription?.unsubscribe();
    this.deviceCommandsSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialCommandTarget']) {
      this.pendingInitialCommandTarget = this.initialCommandTarget;
      this.tryApplyInitialCommandTarget();
    }
  }

  get selectedDevice(): IUserDevice | null {
    return this.devices.find((device) => device.deviceId === this.selectedDeviceId) || null;
  }

  get selectedDeviceCommand(): IUserDeviceCommand | null {
    return this.deviceCommands.find((command) => command.commandId === this.selectedDeviceCommandId) || null;
  }

  get devicesEmptyMessage(): string {
    return this.uid
      ? 'No devices are registered to this account yet.'
      : 'Sign in to inspect registered devices.';
  }

  get desktopRegistrationActionLabel(): string {
    return this.getCurrentDesktopDevice() ? 'Update Computer' : 'Register Computer';
  }

  get isAuthenticated(): boolean {
    return !!this.uid;
  }

  get canOpenCommandsPanel(): boolean {
    return !!this.selectedDevice;
  }

  get canOpenDetailsPanel(): boolean {
    return !!this.selectedDeviceCommand;
  }

  trackDeviceById(index: number, device: IUserDevice): string {
    void index;
    return device.deviceId;
  }

  trackDeviceCommandById(index: number, command: IUserDeviceCommand): string {
    void index;
    return command.commandId;
  }

  trackStringValue(index: number, value: string): string {
    void index;
    return value;
  }

  trackExecutionAttempt(index: number, attempt: DeviceCommandExecutionAttempt): string {
    void index;
    return `${attempt.attempt}-${attempt.startedAt}-${attempt.completedAt}`;
  }

  onAddDeviceTriggerClick(event: Event): void {
    event.stopPropagation();

    const trigger = event.currentTarget as HTMLElement | null;
    if (!trigger || !this.addDevicePopoverTemplate) {
      return;
    }

    this.isAddDevicePopoverOpen = true;
    this.popoverService.open({
      element: trigger,
      placement: 'bottom-right',
      align: 'end',
      bodyTemplate: this.addDevicePopoverTemplate,
      noPadding: true,
      backdrop: true,
      backdropDismiss: true,
      displayMode: this.theme.isLight ? 'light' : 'dark',
      dimensions: {
        width: 360,
        maxWidth: 'calc(100vw - 24px)'
      },
      onClose: () => {
        this.isAddDevicePopoverOpen = false;
      }
    });
  }

  closeAddDevicePopover(): void {
    if (!this.isAddDevicePopoverOpen) {
      return;
    }

    this.isAddDevicePopoverOpen = false;
    this.popoverService.close();
  }

  setMobilePanel(panel: ChatDevicesMobilePanel): void {
    if (panel === 'commands' && !this.canOpenCommandsPanel) {
      return;
    }

    if (panel === 'details' && !this.canOpenDetailsPanel) {
      return;
    }

    this.activeMobilePanel = panel;
  }

  selectDevice(device: IUserDevice, advancePanel = true): void {
    if (!this.uid || this.selectedDeviceId === device.deviceId) {
      return;
    }

    this.selectedDeviceId = device.deviceId;
    this.clearSelectedCommandState();
    this.subscribeToDeviceCommands(device.userId || this.uid, device.deviceId);

    if (advancePanel) {
      this.activeMobilePanel = 'commands';
    }
  }

  selectDeviceCommand(command: IUserDeviceCommand, advancePanel = true): void {
    this.selectedDeviceCommandId = command.commandId;
    this.selectedDeviceCommandLiveOutput$ = command.commandType === 'terminal'
      ? this.desktopDeviceCommandRunnerService.getLiveOutput(command.commandId)
      : undefined;

    if (advancePanel) {
      this.activeMobilePanel = 'details';
    }
  }

  getCurrentDesktopDevice(): IUserDevice | null {
    if (!this.desktopDeviceId) {
      return null;
    }

    return this.devices.find((device) => device.deviceId === this.desktopDeviceId) || null;
  }

  getDesktopRegistrationNote(): string {
    const currentDevice = this.getCurrentDesktopDevice();
    if (currentDevice) {
      return 'This desktop is already registered. Update the name to keep it recognizable for future agent routing.';
    }

    return 'Register this desktop shell so future agents can discover it and route terminal work to the right machine.';
  }

  openBrowserAccessRequest(): void {
    this.closeAddDevicePopover();

    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL('https://platfio.com/contact');
    url.searchParams.set('theme', this.theme.mode);
    window.open(url.toString(), '_blank');
  }

  async registerDesktopDevice(): Promise<void> {
    if (!this.uid) {
      this.toastService.showError('Sign in before registering a computer.');
      return;
    }

    if (!this.isElectronDesktop || !this.desktopDeviceId) {
      this.toastService.showError('Computer registration is only available inside the Platfio desktop app.');
      return;
    }

    const name = this.desktopDeviceName.trim();
    if (!name) {
      this.toastService.showError('Please enter a name for this desktop device.');
      return;
    }

    this.registeringDesktopDevice = true;

    try {
      const existingDevice = this.getCurrentDesktopDevice();

      await this.userDeviceService.upsertDevice(this.uid, {
        deviceId: this.desktopDeviceId,
        name,
        type: 'desktop',
        capabilities: ['terminal'],
        platform: typeof navigator === 'undefined' ? 'Desktop shell' : navigator.userAgent,
        clientVersion: this.nativeHostService.getDesktopMode(),
        agencyId: this.authService.aid || this.uid,
        agencyShared: this.desktopDeviceAgencyShared
      });

      this.closeAddDevicePopover();
      this.toastService.showSuccess(existingDevice ? 'Desktop device updated.' : 'Desktop device registered.');
    } catch (error) {
      console.error('Error registering desktop device from chat modal:', error);
      this.toastService.showError('Unable to register this desktop device right now.');
    } finally {
      this.registeringDesktopDevice = false;
    }
  }

  getDeviceTypeLabel(device: IUserDevice): string {
    return device.type === 'desktop' ? 'Desktop' : 'Browser';
  }

  getDeviceCapabilityLabel(device: IUserDevice): string {
    if (!device.capabilities?.length) {
      return 'No capabilities recorded';
    }

    return device.capabilities
      .map((capability) => capability === 'terminal' ? 'Terminal' : 'Browser Navigation')
      .join(', ');
  }

  getDeviceVisibilityLabel(device: IUserDevice): string {
    return device.agencyShared ? 'Everyone in agency' : 'Only me';
  }

  getDeviceDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (typeof value.toDate === 'function') {
      return value.toDate();
    }

    if (value instanceof Date) {
      return value;
    }

    return new Date(value);
  }

  getDeviceConnectionStatus(device: IUserDevice): 'live' | 'offline' {
    const lastSeenAt = this.getDeviceDate(device.lastSeenAt);
    if (!lastSeenAt) {
      return 'offline';
    }

    return Date.now() - lastSeenAt.getTime() <= 5 * 60 * 1000 ? 'live' : 'offline';
  }

  getDeviceCommandStatusLabel(command: IUserDeviceCommand): string {
    switch (command.status) {
      case 'running':
        return 'Running';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Pending';
    }
  }

  getDeviceCommandSummary(command: IUserDeviceCommand): string {
    return command.result || command.feedback?.summary || 'No execution summary yet.';
  }

  getDeviceCommandFeedbackTags(command: IUserDeviceCommand): string[] {
    const tags: string[] = [];

    if (command.commandType === 'terminal') {
      if (command.feedback?.terminal?.stdout) {
        tags.push('Output');
      }

      if (command.feedback?.terminal?.stderr || command.error) {
        tags.push('Error');
      }

      if (command.feedback?.terminal?.exitCode !== undefined && command.feedback?.terminal?.exitCode !== null) {
        tags.push('Exit code');
      }
    }

    if (command.commandType === 'browser') {
      if (this.getDeviceCommandBrowserScreenshot(command)) {
        tags.push('Screenshot');
      }

      if (command.feedback?.browser?.url) {
        tags.push('URL');
      }

      if (command.feedback?.browser?.title) {
        tags.push('Title');
      }

      if (command.feedback?.browser?.text) {
        tags.push('Text');
      }

      if (command.feedback?.browser?.html) {
        tags.push('HTML');
      }
    }

    if (this.getDeviceCommandFeedbackWarnings(command).length > 0) {
      tags.push('Warnings');
    }

    return tags;
  }

  getDeviceCommandRunner(command: IUserDeviceCommand): string | null {
    const metadata = this.getDeviceCommandMetadataRecord(command);
    return typeof metadata?.['runner'] === 'string' ? metadata['runner'] : null;
  }

  getDeviceCommandBrowserTabId(command: IUserDeviceCommand): number | null {
    const metadata = this.getDeviceCommandMetadataRecord(command);
    return typeof metadata?.['tabId'] === 'number' ? metadata['tabId'] : null;
  }

  getDeviceCommandExecutionMetadata(command: IUserDeviceCommand): DeviceCommandExecutionMetadata | null {
    const metadata = this.getDeviceCommandMetadataRecord(command);
    const execution = metadata?.['execution'];
    return isRecord(execution) ? execution as DeviceCommandExecutionMetadata : null;
  }

  getDeviceCommandExecutionAttempts(command: IUserDeviceCommand): DeviceCommandExecutionAttempt[] {
    const execution = this.getDeviceCommandExecutionMetadata(command);
    if (!Array.isArray(execution?.attempts)) {
      return [];
    }

    return execution.attempts.filter((attempt): attempt is DeviceCommandExecutionAttempt => isRecord(attempt)
      && typeof attempt['attempt'] === 'number'
      && typeof attempt['startedAt'] === 'string'
      && typeof attempt['completedAt'] === 'string'
      && typeof attempt['summary'] === 'string'
      && (attempt['status'] === 'completed' || attempt['status'] === 'failed')
      && typeof attempt['retryable'] === 'boolean');
  }

  getDeviceCommandFeedbackWarnings(command: IUserDeviceCommand): string[] {
    const metadata = this.getDeviceCommandMetadataRecord(command);
    const warnings = metadata?.['feedbackWarnings'];

    if (!Array.isArray(warnings)) {
      return [];
    }

    return warnings.filter((warning): warning is string => typeof warning === 'string' && !!warning.trim());
  }

  isDeviceCommandOutputTruncated(command: IUserDeviceCommand): boolean {
    const metadata = this.getDeviceCommandMetadataRecord(command);
    return metadata?.['outputTruncated'] === true;
  }

  getDeviceCommandTotalOutputCharacters(command: IUserDeviceCommand): number | null {
    const metadata = this.getDeviceCommandMetadataRecord(command);
    return typeof metadata?.['totalOutputCharacters'] === 'number' ? metadata['totalOutputCharacters'] : null;
  }

  getDeviceCommandExecutionStrategyLabel(command: IUserDeviceCommand): string | null {
    const execution = this.getDeviceCommandExecutionMetadata(command);
    if (typeof execution?.strategy !== 'string') {
      return null;
    }

    switch (execution.strategy) {
      case 'always':
        return 'Always retry';
      case 'never':
        return 'No retries';
      default:
        return 'Retry transient failures';
    }
  }

  formatDeviceCommandValue(value: unknown): string {
    if (value === null || value === undefined) {
      return 'Not available';
    }

    if (typeof value === 'string') {
      return value;
    }

    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  getDeviceCommandBrowserScreenshot(command: IUserDeviceCommand): string | null {
    return command.feedback?.browser?.screenshot?.downloadUrl
      || command.feedback?.browser?.screenshot?.dataUrl
      || null;
  }

  private getDeviceCommandMetadataRecord(command: IUserDeviceCommand): Record<string, unknown> | null {
    return isRecord(command.feedback?.metadata) ? command.feedback!.metadata as Record<string, unknown> : null;
  }

  private subscribeToDevices(): void {
    this.devicesSubscription?.unsubscribe();

    if (!this.uid) {
      this.devices = [];
      this.devicesLoading = false;
      this.devicesError = '';
      this.clearSelectedDeviceState();
      return;
    }

    this.devicesLoading = true;
    this.devicesError = '';

    this.devicesSubscription = this.userDeviceService.getAccessibleDevices(this.uid, this.authService.aid || this.uid).subscribe({
      next: (devices) => {
        this.devices = devices || [];
        this.devicesLoading = false;
        this.syncDesktopDeviceName();
        this.tryApplyInitialCommandTarget();

        if (!this.devices.length) {
          this.clearSelectedDeviceState();
          return;
        }

        const selectedDeviceStillExists = this.devices.some((device) => device.deviceId === this.selectedDeviceId);
        if (!selectedDeviceStillExists) {
          this.selectedDeviceId = '';
          this.selectDevice(this.devices[0], false);
        }
      },
      error: (error) => {
        console.error('Error loading chat devices:', error);
        this.devices = [];
        this.devicesLoading = false;
        this.devicesError = 'Unable to load registered devices right now.';
        this.clearSelectedDeviceState();
      }
    });
  }

  private subscribeToDeviceCommands(ownerUid: string, deviceId: string): void {
    this.deviceCommandsSubscription?.unsubscribe();

    if (!ownerUid || !deviceId) {
      this.clearSelectedCommandState();
      return;
    }

    this.deviceCommandsLoading = true;
    this.deviceCommandsError = '';
    this.deviceCommands = [];

    this.deviceCommandsSubscription = this.userDeviceService.getDeviceCommands(ownerUid, deviceId).subscribe({
      next: (commands) => {
        this.deviceCommands = commands || [];
        this.deviceCommandsLoading = false;
        this.tryApplyInitialCommandTarget();

        if (!this.deviceCommands.length) {
          this.clearSelectedCommandState();
          return;
        }

        const selectedCommandStillExists = this.deviceCommands.some((command) => command.commandId === this.selectedDeviceCommandId);
        if (!selectedCommandStillExists) {
          this.selectDeviceCommand(this.deviceCommands[0], false);
          return;
        }

        if (this.selectedDeviceCommand?.commandType === 'terminal') {
          this.selectedDeviceCommandLiveOutput$ = this.desktopDeviceCommandRunnerService.getLiveOutput(this.selectedDeviceCommandId);
        }
      },
      error: (error) => {
        console.error('Error loading device commands:', error);
        this.deviceCommands = [];
        this.deviceCommandsLoading = false;
        this.deviceCommandsError = 'Unable to load device commands right now.';
        this.clearSelectedCommandState();
      }
    });
  }

  private clearSelectedDeviceState(): void {
    this.selectedDeviceId = '';
    this.activeMobilePanel = 'devices';
    this.clearSelectedCommandState();
    this.deviceCommandsSubscription?.unsubscribe();
    this.deviceCommandsSubscription = undefined;
    this.deviceCommands = [];
    this.deviceCommandsLoading = false;
    this.deviceCommandsError = '';
  }

  private clearSelectedCommandState(): void {
    this.selectedDeviceCommandId = '';
    this.selectedDeviceCommandLiveOutput$ = undefined;

    if (this.activeMobilePanel === 'details') {
      this.activeMobilePanel = 'commands';
    }
  }

  private initializeDesktopDeviceDraft(): void {
    this.isElectronDesktop = this.nativeHostService.isElectronDesktop();

    if (!this.isElectronDesktop) {
      this.desktopDeviceId = '';
      this.desktopDeviceAgencyShared = false;
      return;
    }

    this.desktopDeviceId = this.getOrCreateDesktopDeviceId();

    if (!this.desktopDeviceName.trim()) {
      this.desktopDeviceName = this.getDefaultDesktopDeviceName();
    }

    this.syncDesktopDeviceName();
  }

  private getOrCreateDesktopDeviceId(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    const storageKey = 'platfio-desktop-device-id';
    const existingId = window.localStorage.getItem(storageKey);
    if (existingId) {
      return existingId;
    }

    const generatedId = this.generateDeviceId('desktop');
    window.localStorage.setItem(storageKey, generatedId);
    return generatedId;
  }

  private generateDeviceId(prefix: 'desktop' | 'browser'): string {
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    return `${prefix}-${uuid}`;
  }

  private getDefaultDesktopDeviceName(): string {
    if (typeof navigator === 'undefined') {
      return 'My Desktop Device';
    }

    if (navigator.platform.includes('Mac')) {
      return 'My Mac Desktop';
    }

    if (navigator.platform.includes('Win')) {
      return 'My Windows Desktop';
    }

    return 'My Desktop Device';
  }

  private syncDesktopDeviceName(): void {
    const currentDevice = this.getCurrentDesktopDevice();
    const defaultName = this.getDefaultDesktopDeviceName();

    if (currentDevice && (!this.desktopDeviceName.trim() || this.desktopDeviceName === defaultName)) {
      this.desktopDeviceName = currentDevice.name || defaultName;
    }

    this.desktopDeviceAgencyShared = currentDevice?.agencyShared === true;
  }

  private tryApplyInitialCommandTarget(): void {
    const target = this.pendingInitialCommandTarget;
    if (!target || !this.uid || this.devicesLoading) {
      return;
    }

    const device = this.devices.find((entry) => entry.deviceId === target.deviceId);
    if (!device) {
      return;
    }

    if (this.selectedDeviceId !== target.deviceId) {
      this.selectDevice(device, false);
      return;
    }

    if (this.deviceCommandsLoading) {
      return;
    }

    const command = this.deviceCommands.find((entry) => entry.commandId === target.commandId);
    if (!command) {
      return;
    }

    this.selectDeviceCommand(command, false);
    this.activeMobilePanel = 'details';
    this.pendingInitialCommandTarget = null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}